import { Injectable } from '@angular/core';
import { getMonthName } from '../../../core/helper/month';
import { getTranslation } from '../../../core/helper/translation';
import { LocalDate } from '../../../core/model/local-date';
import { AllowSort } from '../../../core/service/factory/allow-sort';
import { ContactDocumentInformationNoteComponent } from '../../component/information-note/contact-document-information-note.component';
import { PropertyDocumentInformationNoteComponent } from '../../component/information-note/property-document-information-note.component';
import { ColumnDefinition, ColumnVisibility } from './../../../core/model/column-definition';
import { ColumnDefinitionFactory } from './../../../core/service/factory/column-definition-factory.service';
import { LanguageService } from '../../../core/service/language.service';

@Injectable()
export class ColumnDefinitionService {
  constructor(
    private readonly _factory: ColumnDefinitionFactory,
    private readonly _languageService: LanguageService
  ) {}

  private getAppendixColumnDefinition() {
    return [
      this._factory.create('APPENDIX.SORTORDER', 'sortOrder', ColumnVisibility.mandatory),
      this._factory.create('APPENDIX.TITLE', 'title', ColumnVisibility.default),
      this._factory.createBoolean('APPENDIX.USE_FOR_DWELLING', 'useForDwelling', ColumnVisibility.default),
      this._factory.createBoolean('APPENDIX.USE_FOR_PARKING', 'useForParking', ColumnVisibility.default),
      this._factory.createBoolean('APPENDIX.COMPANY_LOGO', 'companyLogo', ColumnVisibility.optional),
      this._factory.createBoolean('APPENDIX.LINK_TO_PROPERTY', 'realEstateLevelUsage', ColumnVisibility.optional),
    ];
  }

  private getIndexClauseSettingsColumnDefinition() {
    return [
      this._factory.create('INDEX_CLAUSE_SETTINGS.CAPTION', 'caption', ColumnVisibility.mandatory),
      this._factory.createTranslation('INDEX_CLAUSE_SETTINGS.CALCULATION_BASIS', 'calculationBasisText', {
        visibility: ColumnVisibility.default,
        serverSideSortBinding: 'fromBaseIndex',
      }),
      this._factory.createBoolean('INDEX_CLAUSE_SETTINGS.ALLOW_LOWER_THAN_BASE_INDEX', 'allowLowerThanBaseIndex', ColumnVisibility.default),
      this._factory.createTranslation('INDEX_CLAUSE_SETTINGS.INDEX_SERIES', 'indexSeriesText', {
        visibility: ColumnVisibility.default,
        serverSideSortBinding: 'indexSeries',
      }),
      this._factory.createTranslation('INDEX_CLAUSE_SETTINGS.MIN_PERCENTAGE_CHANGE', 'minPercentageChangeText', {
        visibility: ColumnVisibility.default,
        serverSideSortBinding: 'minPercentageChange',
      }),
      this._factory.createTranslation('INDEX_CLAUSE_SETTINGS.MAX_PERCENTAGE_CHANGE', 'maxPercentageChangeText', {
        visibility: ColumnVisibility.default,
        serverSideSortBinding: 'maxPercentageChange',
      }),
      this._factory.createTranslation('INDEX_CLAUSE_SETTINGS.PERCENTAGE_CHANGE_ROUNDING', 'percentageChangeRoundingText', {
        visibility: ColumnVisibility.default,
        serverSideSortBinding: 'percentageChangeRounding',
      }),
    ];
  }

  private getArticleColumnDefinition() {
    let columns = [
      this._factory.create('ARTICLE_LIST.CODE_COLUMN', 'code'),
      this._factory.create('ARTICLE_LIST.CAPTION_COLUMN', 'caption'),
      this._factory.create('ARTICLE_LIST.INVOICE_TEXT_COLUMN', 'invoiceText'),
      this._factory.createMoney('ARTICLE_LIST.PRICE_COLUMN', 'price', { totalSum: false }),
      this._factory.createTranslation('ARTICLE_LIST.TIME_UNIT_COLUMN', 'timeUnitCaption'),
      this._factory.createTranslation('ARTICLE_LIST.ADJUSTMENT_COLUMN', 'adjustmentCaption'),
      this._factory.create('ARTICLE_GROUP.ARTICLE_GROUP', 'articleGroupName', ColumnVisibility.optional),
      this._factory.createBoolean('ARTICLE.INCLUDE_IN_CONTRACTUAL_RENT', 'includeInContractualRent', ColumnVisibility.optional),
      this._factory.createBoolean('ARTICLE.IS_REDUCTION', 'isReduction', ColumnVisibility.optional),
      this._factory.create('ARTICLE_LIST.INVOICE_ROW_SORT_ORDER_COLUMN', 'invoiceRowSortOrder'),
      this._factory.create('COMMON.INCOME_ACCOUNT', 'incomeAccountNumber'),
      this._factory.create('COMMON.CLAIM_ACCOUNT', 'claimAccountNumber'),
      this._factory.create('COMMON.VAT_ACCOUNT', 'vatAccountNumber'),
      this._factory.create('COMMON.INCOME_PERIODIZATION_ACCOUNT', 'incomePeriodizationAccountNumber', ColumnVisibility.optional),
      this._factory.create('COMMON.RENT_LOSS_ACCOUNT', 'rentLossAccountNumber', ColumnVisibility.optional),
      this._factory.create('COMMON.RENT_LOSS_INCOME_ACCOUNT', 'rentLossIncomeAccountNumber', ColumnVisibility.optional),
      this._factory.createBoolean('ARTICLE.VOLUNTARY_VAT', 'voluntaryVat', ColumnVisibility.optional),
      this._factory.createBoolean('PROPERTY_TAX.IS_PROPERTY_TAX', 'isPropertyTax', ColumnVisibility.optional),
      this._factory.createBoolean('PROPERTY_TAX.IS_RETROACTIVE_FOR_PROPERTY_TAX', 'isRetroactiveForPropertyTax', ColumnVisibility.optional),
    ];
    return columns;
  }

  private getArticleGroupColumnDefinition() {
    let columns = [this._factory.create('ARTICLE_GROUP.NAME', 'name'), this._factory.createBoolean('ARTICLE_GROUP.IS_RENT', 'isRent')];
    return columns;
  }

  private getBuildingColumnDefinition() {
    let columns = [
      this._factory.create('COMMON.NUMBER', 'number'),
      this._factory.create('BUILDING_LIST.NAME_COLUMN', 'name'),
      this._factory.create('BUILDING_LIST.FLOORS_IN_BUILDING_COLUMN', 'floorsInBuilding'),
      this._factory.create('BUILDING_LIST.BUILD_YEAR_COLUMN', 'buildYear'),
      this._factory.create('BUILDING_LIST.RECONSTRUCTION_YEAR_COLUMN', 'reconstructionYear'),
      this._factory.create('COMMON.COMPANY', 'companyName'),
      this._factory.create('COMMON.PROPERTY', 'propertyName'),
      this._factory.create('COMMON.NOTE', 'firstPartOfNote'),
      this._factory.create('COMMON.CENTRAL_LOCATION', 'centralLocation', ColumnVisibility.optional),
      this._factory.create('COMMON.LATITUDE', 'latitude', ColumnVisibility.optional),
      this._factory.create('COMMON.LONGITUDE', 'longitude', ColumnVisibility.optional),
      this._factory.create('AREA.BRA', 'bra', ColumnVisibility.optional),
      this._factory.create('AREA.BOA', 'boa', ColumnVisibility.optional),
      this._factory.create('AREA.LOA', 'loa', ColumnVisibility.optional),
      this._factory.create('AREA.BIA', 'bia', ColumnVisibility.optional),
      this._factory.create('AREA.BTA', 'bta', ColumnVisibility.optional),
      this._factory.create('AREA.OVA', 'ova', ColumnVisibility.optional),
      this._factory.create('AREA.ATEMP', 'atemp', ColumnVisibility.optional),
      this._factory.create('AREA.AGAR', 'agar', ColumnVisibility.optional),
      this._factory.create('AREA.NTA', 'nta', ColumnVisibility.optional),
    ];
    return columns;
  }

  private getBuildingSpaceTypeColumnDefinition() {
    let columns = [
      this._factory.create('BUILDING_SPACE_TYPE.CAPTION', 'caption', ColumnVisibility.mandatory),
      this._factory.create('BUILDING_SPACE_TYPE.BSAB_CODE', 'bsabCode', ColumnVisibility.mandatory),
    ];
    return columns;
  }

  private getCompanyColumnDefinition() {
    let columns = [
      this._factory.create('COMMON.NUMBER', 'number'),
      this._factory.create('COMPANY.NAME', 'name'),
      this._factory.create('COMPANY.ORGANIZATION_NUMBER', 'organizationNo'),
      this._factory.create('COMPANY.EMAIL', 'email'),
      this._factory.createPhoneNumber('COMPANY.PHONE', 'phone'),
      this._factory.create('COMPANY.BANK_GIRO', 'bankgiroNumber', ColumnVisibility.optional),
      this._factory.create('COMPANY.PAYMENT_RECIEVER', 'paymentReceiver', ColumnVisibility.optional),
      this._factory.create('COMPANY.VAT_NUMBER', 'vatNumber', ColumnVisibility.optional),
      this._factory.create('COMPANY.NEXT_INVOICE_NO', 'nextInvoiceNumber', ColumnVisibility.optional),
      this._factory.createDate('COMPANY.ACCOUNTING_LOCKED_TO', 'accountingClosedTo', ColumnVisibility.optional),
      this._factory.create('COMPANY.VER_NO_SERIES_INVOICING', 'verificationNumberSeriesInvoicing', ColumnVisibility.optional),
      this._factory.create('COMPANY.VER_NO_SERIES_GENERAL', 'verificationNumberSeriesGeneral', ColumnVisibility.optional),
      this._factory.create('B2C_SUBS.ISSUER_IDENTITY', 'issuerIdentity', ColumnVisibility.optional),
    ];
    return columns;
  }

  private getControlDataExportColumnDefinition(): ColumnDefinition[] {
    return [
      this._factory.create('COMMON.COMPANY', 'companyName', ColumnVisibility.mandatory),
      this._factory.create('COMMON.INCOME_YEAR', 'incomeYear', ColumnVisibility.mandatory),
      this._factory.createDateTime('CONTROL_DATA.EXPORT_CREATED_AT', 'createdAt', ColumnVisibility.mandatory),
    ];
  }

  private getCooperativeHousingTransferColumnDefinition(): ColumnDefinition[] {
    return [
      this._factory.create('COMMON.COMPANY', 'companyName', ColumnVisibility.mandatory),
      this._factory.create('RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL', 'rentalId', ColumnVisibility.mandatory),
      this._factory.createDate('LEASE_AGREEMENT.AGREEMENT_DATE', 'transferDate', ColumnVisibility.mandatory),
      this._factory.create('COOPERATIVE_HOUSING.TRANSFERRED_SHARE', 'totalShare', ColumnVisibility.optional),
      this._factory.createMoney('COMMON.AMOUNT', 'amount', ColumnVisibility.optional),
      this._factory.create('COOPERATIVE_HOUSING.TRANSFER_SELLERS', 'sellers', ColumnVisibility.optional),
      this._factory.create('COOPERATIVE_HOUSING.TRANSFER_BUYERS', 'buyers', ColumnVisibility.optional),
      this._factory.create('COOPERATIVE_HOUSING.ERROR_MESSAGE', 'errorMessage', ColumnVisibility.optional),
      this._factory.create('COMMON.ADDRESS', 'rentalObjectAddress', ColumnVisibility.optional),
    ];
  }

  private getCustomPropertyColumnDefinition() {
    return [
      this._factory.create('CUSTOM_PROPERTY.CAPTION', 'caption'),
      this._factory.createTranslation('CUSTOM_PROPERTY.DATATYPECAPTION', 'dataTypeCaption'),
    ];
  }

  private getLettingFeatureAdminColumnDefinition() {
    return [
      this._factory.create('LETTING_FEATURE.CAPTION', 'caption', ColumnVisibility.mandatory),
      this._factory.createBoolean('LETTING_FEATURE.IS_ACTIVE', 'isActive', ColumnVisibility.mandatory),
    ];
  }

  private getObjectComponentCategoryColumnDefinition() {
    return [this._factory.create('COMMON.CAPTION', 'name')];
  }

  private getContractBlockColumnDefinition() {
    let columns = [
      this._factory.createTranslation('LEASE_AGREEMENT.STATUS', 'statusCaption', { allowSort: AllowSort.No }),
      this._factory.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber'),
      this._factory.create('LEASE_AGREEMENT.LEASE_AGREEMENT_SUB_CATEGORY', 'subCategoryCaption', { allowSort: AllowSort.No }),
      this._factory.create('LEASE_AGREEMENT.POPULAR_NAME', 'popularName', ColumnVisibility.optional),
      this._factory.createNumber('LEASE_AGREEMENT.LEASE_AGREEMENT_AREA', 'leaseAgreementArea', ColumnVisibility.optional),
      this._factory.createNumber('LEASE_AGREEMENT.LEASE_AGREEMENT_SUM_RENTAL_OBJECT_AREA', 'rentalObjectSumSurface', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE', 'tenantOne'),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_NUMBER', 'tenantOneNumber', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_CIVICNUMBER', 'tenantOneContactIdentification', ColumnVisibility.optional),
      this._factory.createPhoneNumber('LEASE_AGREEMENT.TENANT_ONE_PHONE', 'tenantOnePhone', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_EMAIL', 'tenantOneEmail', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_NOTE', 'tenantOneFirstPartOfNote', ColumnVisibility.optional),
      this._factory.create('COMMON.COMPANY', 'companyNumberAndName', ColumnVisibility.optional),
      this._factory.create('COMMON.PROPERTY', 'propertyNumberAndName', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT.AGREEMENT_DATE', 'agreementDate', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT.FROM_DATE', 'fromDate', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.TO_DATE_LABEL', 'toDate', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT.TO_DATE', 'lastBillingDate', ColumnVisibility.optional),
      this._factory.createDate('TERMINATE_LEASE_AGREEMENT_FORM.TERMINATED_DATE_LABEL', 'terminationDate', ColumnVisibility.optional),
      this._factory.create('TERMINATE_LEASE_AGREEMENT_FORM.TERMINATED_BY_LABEL', 'terminatedByText', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT.VALID_CONTRACT_PERIOD_FROM', 'validContractPeriodFromLandlord', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createDate('LEASE_AGREEMENT.VALID_CONTRACT_PERIOD_TO', 'validContractPeriodToLandlord', { allowSort: AllowSort.No }),
      this._factory.createDate('LEASE_AGREEMENT.LATEST_TERMINATION_DATE', 'latestTerminationDateLandlord', { allowSort: AllowSort.No }),
      this._factory.create('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.LANDLORD_LABEL', 'noticePeriodLandlord'),
      this._factory.create('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.TENANT_LABEL', 'noticePeriodTenant', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.PROLONGATION_TIME_LABEL', 'prolongationTime', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.INVOICE_DELIVERY_METHOD', 'invoiceDeliveryMethodText', ColumnVisibility.optional),
      this._factory.create('INVOICE.PREVIEW.INVOICE_ADDRESS', 'invoiceAddressString', ColumnVisibility.optional),
      this._factory.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT_CURRENT', 'currentPeriodRentAmount', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT_NEXT', 'nextPeriodRentAmount', { allowSort: AllowSort.No }),
      this._factory.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT_INCL_VAT_NEXT', 'nextPeriodRentAmountInclVat', { allowSort: AllowSort.No }),
      this._factory.create('LEASE_AGREEMENT.INVOICE_PERIOD', 'invoicePeriodText', ColumnVisibility.optional),
      this._factory.createMoney('COMMON.YEAR_AMOUNT', 'totalYearRent', { allowSort: AllowSort.No }),
      this._factory.createMoney('COMMON.YEAR_AMOUNT_INCL_VAT', 'totalYearRentInclVat', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('COMMON.YEAR_AMOUNT_PER_SQM', 'totalYearRentPerSqm', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('COMMON.YEAR_RENT', 'articleGroupRentPerYear', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('COMMON.YEAR_RENT_PER_SQM', 'articleGroupRentPerYearPerSqm', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('COMMON.YEAR_ADDITION', 'articleGroupAdditionPerYear', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('LEASE_AGREEMENT_ROW.YEAR_AMOUNT_IN_CURRENT_YEAR', 'chargedYearRentThisYear', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('LEASE_AGREEMENT_ROW.YEAR_AMOUNT_IN_CURRENT_YEAR_INCL_VAT', 'chargedYearRentThisYearInclVat', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.create('LEASE_AGREEMENT.HAS_INDEX_RENT', 'hasIndexRent', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('LEASE_AGREEMENT.WAULT_RENT', 'waultRent', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createNumber('LEASE_AGREEMENT.WAULT', 'wault', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('LEASE_AGREEMENT.WAULT_TO_EXPIRY', 'waultToExpiry', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createTranslation('LEASE_AGREEMENT.DIRECT_DEBIT', 'directDebitStatusCaption', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.create('COMMON.NOTE', 'firstPartOfNote', ColumnVisibility.optional),
    ];
    return columns;
  }

  private getContractParkingColumnDefinition() {
    let columns = [
      this._factory.createTranslation('LEASE_AGREEMENT.STATUS', 'statusCaption', { allowSort: AllowSort.No }),
      this._factory.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber'),
      this._factory.create('RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL', 'rentalObjectRentalId', ColumnVisibility.optional),
      this._factory.create('RENTAL_OBJECT_LIST.PARKING_NUMBER_COLUMN', 'rentalObjectParkingNumber', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE', 'tenantOne'),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_NUMBER', 'tenantOneNumber', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_CIVICNUMBER', 'tenantOneContactIdentification', ColumnVisibility.optional),
      this._factory.createPhoneNumber('LEASE_AGREEMENT.TENANT_ONE_PHONE', 'tenantOnePhone', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_EMAIL', 'tenantOneEmail', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_NOTE', 'tenantOneFirstPartOfNote', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_TWO', 'tenantTwo', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_TWO_NUMBER', 'tenantTwoNumber', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_TWO_CIVICNUMBER', 'tenantTwoContactIdentification', ColumnVisibility.optional),
      this._factory.createPhoneNumber('LEASE_AGREEMENT.TENANT_TWO_PHONE', 'tenantTwoPhone', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_TWO_EMAIL', 'tenantTwoEmail', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.STREET', 'street'),
      this._factory.create('LEASE_AGREEMENT.RENTAL_OBJECT_TYPE', 'rentalObjectType'),
      this._factory.createNumber('RENTAL_OBJECT_FORM.SURFACE_LABEL', 'rentalObjectSurface', {
        visibility: ColumnVisibility.optional,
        totalSum: true,
      }),
      this._factory.create('COMMON.COMPANY', 'companyNumberAndName', ColumnVisibility.optional),
      this._factory.create('COMMON.PROPERTY', 'propertyNumberAndName', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT.AGREEMENT_DATE', 'agreementDate', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT.FROM_DATE', 'fromDate'),
      this._factory.createDate('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.TO_DATE_LABEL', 'toDate', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT.TO_DATE', 'lastBillingDate'),
      this._factory.createDate('TERMINATE_LEASE_AGREEMENT_FORM.TERMINATED_DATE_LABEL', 'terminationDate', ColumnVisibility.optional),
      this._factory.create('TERMINATE_LEASE_AGREEMENT_FORM.TERMINATED_BY_LABEL', 'terminatedByText', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT.VALID_CONTRACT_PERIOD_FROM', 'validContractPeriodFromLandlord', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createDate('LEASE_AGREEMENT.VALID_CONTRACT_PERIOD_TO', 'validContractPeriodToLandlord', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createDate('LEASE_AGREEMENT.LATEST_TERMINATION_DATE', 'latestTerminationDateLandlord', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.create('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.LANDLORD_LABEL', 'noticePeriodLandlord', ColumnVisibility.optional),
      this._factory.createDate('TERMINATE_LEASE_AGREEMENT_FORM.DESIRED_REMOVAL_LABEL', 'desiredMoveOutDate', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT.GIVE_UP_SECURITY_OF_TENURE_TO', 'giveUpSecurityOfTenureTo', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.INVOICE_DELIVERY_METHOD', 'invoiceDeliveryMethodText', ColumnVisibility.optional),
      this._factory.create('INVOICE.PREVIEW.INVOICE_ADDRESS', 'invoiceAddressString', ColumnVisibility.optional),
      this._factory.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT_CURRENT', 'currentPeriodRentAmount', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT_NEXT', 'nextPeriodRentAmount', { allowSort: AllowSort.No }),
      this._factory.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT_INCL_VAT_NEXT', 'nextPeriodRentAmountInclVat', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.create('LEASE_AGREEMENT.INVOICE_PERIOD', 'invoicePeriodText', ColumnVisibility.optional),
      this._factory.createMoney('COMMON.YEAR_AMOUNT', 'totalYearRent', { allowSort: AllowSort.No }),
      this._factory.createMoney('COMMON.YEAR_AMOUNT_INCL_VAT', 'totalYearRentInclVat', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('COMMON.YEAR_AMOUNT_PER_SQM', 'totalYearRentPerSqm', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('COMMON.YEAR_RENT', 'articleGroupRentPerYear', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('COMMON.YEAR_RENT_PER_SQM', 'articleGroupRentPerYearPerSqm', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('COMMON.YEAR_ADDITION', 'articleGroupAdditionPerYear', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('LEASE_AGREEMENT_ROW.YEAR_AMOUNT_IN_CURRENT_YEAR', 'chargedYearRentThisYear', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('LEASE_AGREEMENT_ROW.YEAR_AMOUNT_IN_CURRENT_YEAR_INCL_VAT', 'chargedYearRentThisYearInclVat', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.create('LEASE_AGREEMENT.HAS_INDEX_RENT', 'hasIndexRent', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createTranslation(
        'LEASE_AGREEMENT.DIRECT_DEBIT',
        'directDebitStatusCaption',
        { allowSort: AllowSort.No, visibility: ColumnVisibility.optional } // TODO: Sortable?
      ),
      this._factory.create('COMMON.NOTE', 'firstPartOfNote', ColumnVisibility.optional),
    ];
    return columns;
  }

  private getContractCooperativeHousingColumnDefinition() {
    let columns = [
      this._factory.createTranslation('LEASE_AGREEMENT.STATUS', 'statusCaption'),
      this._factory.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber'),
      this._factory.create('RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL', 'rentalObjectRentalId', ColumnVisibility.optional),
      this._factory.create('COOPERATIVE_HOUSING.OWNER_ONE_NAME', 'tenantOne'),
      this._factory.create('COOPERATIVE_HOUSING.OWNER_ONE_NUMBER', 'tenantOneNumber', ColumnVisibility.optional),
      this._factory.create('COOPERATIVE_HOUSING.OWNER_ONE_CIVICNUMBER', 'tenantOneContactIdentification', ColumnVisibility.optional),
      this._factory.createPhoneNumber('COOPERATIVE_HOUSING.OWNER_ONE_PHONE', 'tenantOnePhone', ColumnVisibility.optional),
      this._factory.create('COOPERATIVE_HOUSING.OWNER_ONE_EMAIL', 'tenantOneEmail', ColumnVisibility.optional),
      this._factory.create('COOPERATIVE_HOUSING.OWNER_ONE_NOTE', 'tenantOneFirstPartOfNote', ColumnVisibility.optional),
      this._factory.create('COOPERATIVE_HOUSING.OWNER_TWO_NAME', 'tenantTwo', ColumnVisibility.optional),
      this._factory.create('COOPERATIVE_HOUSING.OWNER_TWO_NUMBER', 'tenantTwoNumber', ColumnVisibility.optional),
      this._factory.create('COOPERATIVE_HOUSING.OWNER_TWO_CIVICNUMBER', 'tenantTwoContactIdentification', ColumnVisibility.optional),
      this._factory.createPhoneNumber('COOPERATIVE_HOUSING.OWNER_TWO_PHONE', 'tenantTwoPhone', ColumnVisibility.optional),
      this._factory.create('COOPERATIVE_HOUSING.OWNER_TWO_EMAIL', 'tenantTwoEmail', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.STREET', 'street'),
      this._factory.create('LEASE_AGREEMENT.RENTAL_OBJECT_TYPE', 'rentalObjectType'),
      this._factory.createNumber('RENTAL_OBJECT_FORM.SURFACE_LABEL', 'rentalObjectSurface', {
        visibility: ColumnVisibility.optional,
        totalSum: true,
      }),
      this._factory.create('COMMON.COMPANY', 'companyNumberAndName', ColumnVisibility.optional),
      this._factory.create('COMMON.PROPERTY', 'propertyNumberAndName', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT.FROM_DATE', 'fromDate'),
      this._factory.createDate('LEASE_AGREEMENT.TO_DATE', 'lastBillingDate'),
      this._factory.createDate('LEASE_AGREEMENT.AGREEMENT_DATE', 'agreementDate', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.INVOICE_DELIVERY_METHOD', 'invoiceDeliveryMethodText', ColumnVisibility.optional),
      this._factory.create('INVOICE.PREVIEW.INVOICE_ADDRESS', 'invoiceAddressString', ColumnVisibility.optional),
      this._factory.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT_CURRENT', 'currentPeriodRentAmount', ColumnVisibility.optional),
      this._factory.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT_NEXT', 'nextPeriodRentAmount'),
      this._factory.create('LEASE_AGREEMENT.INVOICE_PERIOD', 'invoicePeriodText', ColumnVisibility.optional),
      this._factory.createMoney('COMMON.YEAR_AMOUNT', 'totalYearRent'),
      this._factory.createMoney('COMMON.YEAR_AMOUNT_PER_SQM', 'totalYearRentPerSqm', ColumnVisibility.optional),
      this._factory.createMoney('LEASE_AGREEMENT_ROW.YEAR_AMOUNT_IN_CURRENT_YEAR', 'chargedYearRentThisYear', ColumnVisibility.optional),
      this._factory.createTranslation('LEASE_AGREEMENT.DIRECT_DEBIT', 'directDebitStatusCaption', ColumnVisibility.optional),
      this._factory.createTranslation('SUBLEASE_REQUEST.SUBLEASE', 'subleaseInformation', ColumnVisibility.optional),
      this._factory.createDate('SUBLEASE_REQUEST.FROMDATE', 'validSubleaseFrom', ColumnVisibility.optional),
      this._factory.createDate('SUBLEASE_REQUEST.TODATE', 'validSubleaseTo', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.CREDITOR_EXIST', 'hasCreditorContact', ColumnVisibility.optional),
      this._factory.create('COOPERATIVE_HOUSING.ECONOMICAL_PLAN_NUMBER', 'economicalPlanNumber', ColumnVisibility.optional),
      this._factory.create('COMMON.NOTE', 'firstPartOfNote', ColumnVisibility.optional),
    ];
    return columns;
  }

  private getContractDwellingColumnDefinition() {
    let columns = [
      this._factory.createTranslation('LEASE_AGREEMENT.STATUS', 'statusCaption', { allowSort: AllowSort.No }),
      this._factory.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber'),
      this._factory.create('RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL', 'rentalObjectRentalId', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE', 'tenantOne'),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_NUMBER', 'tenantOneNumber', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_CIVICNUMBER', 'tenantOneContactIdentification', ColumnVisibility.optional),
      this._factory.createPhoneNumber('LEASE_AGREEMENT.TENANT_ONE_PHONE', 'tenantOnePhone', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_EMAIL', 'tenantOneEmail', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_NOTE', 'tenantOneFirstPartOfNote', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_TWO', 'tenantTwo', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_TWO_NUMBER', 'tenantTwoNumber', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_TWO_CIVICNUMBER', 'tenantTwoContactIdentification', ColumnVisibility.optional),
      this._factory.createPhoneNumber('LEASE_AGREEMENT.TENANT_TWO_PHONE', 'tenantTwoPhone', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_TWO_EMAIL', 'tenantTwoEmail', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.STREET', 'street'),
      this._factory.create('LEASE_AGREEMENT.RENTAL_OBJECT_TYPE', 'rentalObjectType'),
      this._factory.createNumber('RENTAL_OBJECT_FORM.SURFACE_LABEL', 'rentalObjectSurface', {
        visibility: ColumnVisibility.optional,
        totalSum: true,
      }),
      this._factory.create('COMMON.COMPANY', 'companyNumberAndName', { visibility: ColumnVisibility.optional }),
      this._factory.create('COMMON.PROPERTY', 'propertyNumberAndName', { visibility: ColumnVisibility.optional }),
      this._factory.createDate('LEASE_AGREEMENT.AGREEMENT_DATE', 'agreementDate', { visibility: ColumnVisibility.optional }),
      this._factory.createDate('LEASE_AGREEMENT.FROM_DATE', 'fromDate'),
      this._factory.createDate('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.TO_DATE_LABEL', 'toDate', { visibility: ColumnVisibility.optional }),
      this._factory.createDate('LEASE_AGREEMENT.TO_DATE', 'lastBillingDate'),
      this._factory.createDate('TERMINATE_LEASE_AGREEMENT_FORM.TERMINATED_DATE_LABEL', 'terminationDate', {
        visibility: ColumnVisibility.optional,
      }),
      this._factory.create('TERMINATE_LEASE_AGREEMENT_FORM.TERMINATED_BY_LABEL', 'terminatedByText', {
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createDate('TERMINATE_LEASE_AGREEMENT_FORM.DESIRED_REMOVAL_LABEL', 'desiredMoveOutDate', {
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createDate('LEASE_AGREEMENT.GIVE_UP_SECURITY_OF_TENURE_TO', 'giveUpSecurityOfTenureTo', {
        visibility: ColumnVisibility.optional,
      }),
      this._factory.create('LEASE_AGREEMENT.INVOICE_DELIVERY_METHOD', 'invoiceDeliveryMethodText', {
        visibility: ColumnVisibility.optional,
      }),
      this._factory.create('INVOICE.PREVIEW.INVOICE_ADDRESS', 'invoiceAddressString', ColumnVisibility.optional),
      this._factory.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT_CURRENT', 'currentPeriodRentAmount', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT_NEXT', 'nextPeriodRentAmount', { allowSort: AllowSort.No }),
      this._factory.create('LEASE_AGREEMENT.INVOICE_PERIOD', 'invoicePeriodText', { visibility: ColumnVisibility.optional }),
      this._factory.createMoney('COMMON.YEAR_AMOUNT', 'totalYearRent', { allowSort: AllowSort.No }),
      this._factory.createMoney('COMMON.YEAR_AMOUNT_PER_SQM', 'totalYearRentPerSqm', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('COMMON.YEAR_RENT', 'articleGroupRentPerYear', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('COMMON.YEAR_RENT_PER_SQM', 'articleGroupRentPerYearPerSqm', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('COMMON.YEAR_ADDITION', 'articleGroupAdditionPerYear', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('RENTAL_OBJECT_LIST.NEGOTIATION_YEAR_RENT', 'yearRentNegotiation', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('RENTAL_OBJECT_LIST.NEGOTIATION_YEAR_RENT_PER_SQM', 'yearRentNegotiationPerSqm', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('LEASE_AGREEMENT_ROW.YEAR_AMOUNT_IN_CURRENT_YEAR', 'chargedYearRentThisYear', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('COMMON.NORMRENT', 'normRentContractRH91', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('RENTAL_OBJECT_LIST.BVK91RENT', 'rentContractBVK', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createTranslation(
        'LEASE_AGREEMENT.DIRECT_DEBIT',
        'directDebitStatusCaption',
        { allowSort: AllowSort.No, visibility: ColumnVisibility.optional } // TODO: Sortable?
      ),
      this._factory.createTranslation('SUBLEASE_REQUEST.SUBLEASE', 'subleaseInformation', { visibility: ColumnVisibility.optional }),
      this._factory.createDate('SUBLEASE_REQUEST.FROMDATE', 'validSubleaseFrom', { visibility: ColumnVisibility.optional }),
      this._factory.createDate('SUBLEASE_REQUEST.TODATE', 'validSubleaseTo', { visibility: ColumnVisibility.optional }),
      this._factory.create('LEASE_AGREEMENT.CREDITOR_EXIST', 'hasCreditorContact', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }), // TODO: Sortable?
      this._factory.create('COMMON.NOTE', 'firstPartOfNote', ColumnVisibility.optional),
    ];
    return columns;
  }

  private getContractPremisesColumnDefinition() {
    let columns = [
      this._factory.createTranslation('LEASE_AGREEMENT.STATUS', 'statusCaption', { allowSort: AllowSort.No }),
      this._factory.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber'),
      this._factory.create('LEASE_AGREEMENT.POPULAR_NAME', 'popularName', ColumnVisibility.optional),
      this._factory.create('RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL', 'rentalObjectRentalId', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE', 'tenantOne'),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_NUMBER', 'tenantOneNumber', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_CIVICNUMBER', 'tenantOneContactIdentification', ColumnVisibility.optional),
      this._factory.createPhoneNumber('LEASE_AGREEMENT.TENANT_ONE_PHONE', 'tenantOnePhone', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_EMAIL', 'tenantOneEmail', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_NOTE', 'tenantOneFirstPartOfNote', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_TWO', 'tenantTwo', ColumnVisibility.optional),
      this._factory.createPhoneNumber('LEASE_AGREEMENT.TENANT_TWO_PHONE', 'tenantTwoPhone', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_TWO_EMAIL', 'tenantTwoEmail', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.STREET', 'street'),
      this._factory.create('LEASE_AGREEMENT.RENTAL_OBJECT_TYPE', 'rentalObjectType'),
      this._factory.createNumber('RENTAL_OBJECT_FORM.SURFACE_LABEL', 'rentalObjectSurface', {
        visibility: ColumnVisibility.optional,
        totalSum: true,
      }),
      this._factory.create('COMMON.COMPANY', 'companyNumberAndName', ColumnVisibility.optional),
      this._factory.create('COMMON.PROPERTY', 'propertyNumberAndName', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT.AGREEMENT_DATE', 'agreementDate', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT.FROM_DATE', 'fromDate', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.TO_DATE_LABEL', 'toDate', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT.TO_DATE', 'lastBillingDate', ColumnVisibility.optional),
      this._factory.createDate('TERMINATE_LEASE_AGREEMENT_FORM.TERMINATED_DATE_LABEL', 'terminationDate', ColumnVisibility.optional),
      this._factory.create('TERMINATE_LEASE_AGREEMENT_FORM.TERMINATED_BY_LABEL', 'terminatedByText', ColumnVisibility.optional),
      this._factory.createDate('TERMINATE_LEASE_AGREEMENT_FORM.DESIRED_REMOVAL_LABEL', 'desiredMoveOutDate', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT.GIVE_UP_SECURITY_OF_TENURE_TO', 'giveUpSecurityOfTenureTo', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT.VALID_CONTRACT_PERIOD_FROM', 'validContractPeriodFromLandlord', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createDate('LEASE_AGREEMENT.VALID_CONTRACT_PERIOD_TO', 'validContractPeriodToLandlord', { allowSort: AllowSort.No }),
      this._factory.createDate('LEASE_AGREEMENT.LATEST_TERMINATION_DATE', 'latestTerminationDateLandlord', { allowSort: AllowSort.No }),
      this._factory.create('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.LANDLORD_LABEL', 'noticePeriodLandlord'),
      this._factory.create('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.TENANT_LABEL', 'noticePeriodTenant', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.PROLONGATION_TIME_LABEL', 'prolongationTime', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.INVOICE_DELIVERY_METHOD', 'invoiceDeliveryMethodText', ColumnVisibility.optional),
      this._factory.create('INVOICE.PREVIEW.INVOICE_ADDRESS', 'invoiceAddressString', ColumnVisibility.optional),
      this._factory.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT_CURRENT', 'currentPeriodRentAmount', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT_NEXT', 'nextPeriodRentAmount', { allowSort: AllowSort.No }),
      this._factory.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT_INCL_VAT_NEXT', 'nextPeriodRentAmountInclVat', { allowSort: AllowSort.No }),
      this._factory.create('LEASE_AGREEMENT.INVOICE_PERIOD', 'invoicePeriodText', ColumnVisibility.optional),
      this._factory.createMoney('COMMON.YEAR_AMOUNT', 'totalYearRent', { allowSort: AllowSort.No }),
      this._factory.createMoney('COMMON.YEAR_AMOUNT_INCL_VAT', 'totalYearRentInclVat', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('COMMON.YEAR_AMOUNT_PER_SQM', 'totalYearRentPerSqm', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('COMMON.YEAR_RENT', 'articleGroupRentPerYear', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('COMMON.YEAR_RENT_PER_SQM', 'articleGroupRentPerYearPerSqm', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('COMMON.YEAR_ADDITION', 'articleGroupAdditionPerYear', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('LEASE_AGREEMENT_ROW.YEAR_AMOUNT_IN_CURRENT_YEAR', 'chargedYearRentThisYear', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('LEASE_AGREEMENT_ROW.YEAR_AMOUNT_IN_CURRENT_YEAR_INCL_VAT', 'chargedYearRentThisYearInclVat', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.create('LEASE_AGREEMENT.HAS_INDEX_RENT', 'hasIndexRent', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createNumber('LEASE_AGREEMENT.WAULT', 'wault', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('LEASE_AGREEMENT.WAULT_TO_EXPIRY', 'waultToExpiry', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createTranslation('LEASE_AGREEMENT.DIRECT_DEBIT', 'directDebitStatusCaption', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.create('COMMON.NOTE', 'firstPartOfNote', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.CREDITOR_EXIST', 'hasCreditorContact', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
    ];
    return columns;
  }

  private getContractOtherColumnDefinition() {
    let columns = [
      this._factory.createTranslation('LEASE_AGREEMENT.STATUS', 'statusCaption', { allowSort: AllowSort.No }),
      this._factory.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber'),
      this._factory.create('RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL', 'rentalObjectRentalId', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE', 'tenantOne'),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_NUMBER', 'tenantOneNumber', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_CIVICNUMBER', 'tenantOneContactIdentification', ColumnVisibility.optional),
      this._factory.createPhoneNumber('LEASE_AGREEMENT.TENANT_ONE_PHONE', 'tenantOnePhone', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_EMAIL', 'tenantOneEmail', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_NOTE', 'tenantOneFirstPartOfNote', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.STREET', 'street'),
      this._factory.create('LEASE_AGREEMENT.RENTAL_OBJECT_TYPE', 'rentalObjectType'),
      this._factory.createNumber('RENTAL_OBJECT_FORM.SURFACE_LABEL', 'rentalObjectSurface', {
        visibility: ColumnVisibility.optional,
        totalSum: true,
      }),
      this._factory.create('COMMON.COMPANY', 'companyNumberAndName', ColumnVisibility.optional),
      this._factory.create('COMMON.PROPERTY', 'propertyNumberAndName', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT.AGREEMENT_DATE', 'agreementDate', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT.FROM_DATE', 'fromDate', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.TO_DATE_LABEL', 'toDate', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT.TO_DATE', 'lastBillingDate', ColumnVisibility.optional),
      this._factory.createDate('TERMINATE_LEASE_AGREEMENT_FORM.TERMINATED_DATE_LABEL', 'terminationDate', ColumnVisibility.optional),
      this._factory.create('TERMINATE_LEASE_AGREEMENT_FORM.TERMINATED_BY_LABEL', 'terminatedByText', ColumnVisibility.optional),
      this._factory.createDate('TERMINATE_LEASE_AGREEMENT_FORM.DESIRED_REMOVAL_LABEL', 'desiredMoveOutDate', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT.VALID_CONTRACT_PERIOD_FROM', 'validContractPeriodFromLandlord', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createDate('LEASE_AGREEMENT.VALID_CONTRACT_PERIOD_TO', 'validContractPeriodToLandlord', { allowSort: AllowSort.No }),
      this._factory.createDate('LEASE_AGREEMENT.LATEST_TERMINATION_DATE', 'latestTerminationDateLandlord', { allowSort: AllowSort.No }),
      this._factory.create('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.LANDLORD_LABEL', 'noticePeriodLandlord'),
      this._factory.create('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.TENANT_LABEL', 'noticePeriodTenant', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.PROLONGATION_TIME_LABEL', 'prolongationTime', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.INVOICE_DELIVERY_METHOD', 'invoiceDeliveryMethodText', ColumnVisibility.optional),
      this._factory.create('INVOICE.PREVIEW.INVOICE_ADDRESS', 'invoiceAddressString', ColumnVisibility.optional),
      this._factory.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT_CURRENT', 'currentPeriodRentAmount', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT_NEXT', 'nextPeriodRentAmount', { allowSort: AllowSort.No }),
      this._factory.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT_INCL_VAT_NEXT', 'nextPeriodRentAmountInclVat', { allowSort: AllowSort.No }),
      this._factory.create('LEASE_AGREEMENT.INVOICE_PERIOD', 'invoicePeriodText', ColumnVisibility.optional),
      this._factory.createMoney('COMMON.YEAR_AMOUNT', 'totalYearRent', { allowSort: AllowSort.No }),
      this._factory.createMoney('COMMON.YEAR_AMOUNT_INCL_VAT', 'totalYearRentInclVat', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('COMMON.YEAR_AMOUNT_PER_SQM', 'totalYearRentPerSqm', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('COMMON.YEAR_RENT', 'articleGroupRentPerYear', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('COMMON.YEAR_RENT_PER_SQM', 'articleGroupRentPerYearPerSqm', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('COMMON.YEAR_ADDITION', 'articleGroupAdditionPerYear', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('LEASE_AGREEMENT_ROW.YEAR_AMOUNT_IN_CURRENT_YEAR', 'chargedYearRentThisYear', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('LEASE_AGREEMENT_ROW.YEAR_AMOUNT_IN_CURRENT_YEAR_INCL_VAT', 'chargedYearRentThisYearInclVat', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.create('LEASE_AGREEMENT.HAS_INDEX_RENT', 'hasIndexRent', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createNumber('LEASE_AGREEMENT.WAULT', 'wault', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createMoney('LEASE_AGREEMENT.WAULT_TO_EXPIRY', 'waultToExpiry', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.createTranslation('LEASE_AGREEMENT.DIRECT_DEBIT', 'directDebitStatusCaption', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      this._factory.create('COMMON.NOTE', 'firstPartOfNote', ColumnVisibility.optional),
    ];
    return columns;
  }

  private getPriceListItemColumnDefinition() {
    return [
      this._factory.create('PRICELIST.CODE', 'code'),
      this._factory.create('PRICELIST.PRICELIST_ITEM_NAME', 'name'),
      this._factory.create('COMPONENT.COMPONENT_CATEGORY', 'componentCategoryName'),
      this._factory.create('COMPONENT.TYPE', 'componentTypeName'),
      this._factory.createMoney('PRICELIST.PRICE_PER_UNIT', 'amount', { totalSum: false }),
      this._factory.createTranslation('ARTICLE.UNIT', 'unitCaption'),
      this._factory.createMoney('PRICELIST.START_COST', 'startCost', { totalSum: false, visibility: ColumnVisibility.optional }),
    ];
  }

  private getLogColumnDefinition() {
    return [
      this._factory.createDateTime('LOG.CREATED_AT', 'createdAt', ColumnVisibility.mandatory),
      this._factory.create('LOG.SHORT_TEXT', 'shortText', ColumnVisibility.mandatory),
      this._factory.create('LOG.DETAILS', 'fullText', ColumnVisibility.mandatory),
      this._factory.create('LOG.USERNAME', 'userName', ColumnVisibility.default),
    ];
  }

  private getLeaseAgreementImdConsumptionColumnDefinition() {
    return [
      this._factory.createDate('CONSUMPTION.CONSUMPTION_DATE', 'consumptionDate', ColumnVisibility.mandatory),
      this._factory.create('CONSUMPTION.MEDIA_TYPE.TYPE', 'meterTypeDescription'),
      this._factory.createDecimal('CONSUMPTION.CONSUMPTION', 'consumption', ColumnVisibility.default),
      this._factory.create('CONSUMPTION.METER.METER', 'imdMeterName', ColumnVisibility.optional),
      this._factory.createDecimal('CONSUMPTION.CONSUMPTION_METER_SHARE', 'meterShare', ColumnVisibility.optional),
      this._factory.create('INVOICE.INVOICE_NUMBER', 'invoiceNumber'),
      this._factory.create('INVOICE.INVOICED_PERIOD', 'invoicePeriod'),
    ];
  }

  private getPriceListColumnDefinition() {
    return [
      this._factory.create('PRICELIST.PRICELIST', 'name'),
      this._factory.createTranslation('PRICELIST.PRICELIST_TYPE', 'typeCaption', { serverSideSortBinding: 'listType' }),
      this._factory.create('COMMON.CURRENCY', 'currencyCode'),
    ];
  }

  private getPropertyColumnDefinition() {
    let tran = this._languageService.getCurrentTranslations();
    let columns = [
      this._factory.create('PROPERTY.NUMBER', 'propertyNumber'),
      this._factory.create('PROPERTY.NAME', 'propertyName'),
      this._factory.create('PROPERTY.MUNICIPALITY', 'municipality'),
      this._factory.create('COMMON.COMPANY_NUMBER', 'companyNumber', ColumnVisibility.optional),
      this._factory.create('PROPERTY.COMPANY', 'companyName'),
      this._factory.create('PROPERTY.COST_PLACE', 'costPlace'),
      this._factory.create('PROPERTY.COST_BEARER', 'costBearer', ColumnVisibility.optional),
      this._factory.create('COMMON.NOTE', 'firstPartOfNote'),
      this._factory.create('PROPERTY.FAULTNOTIFICATION_RECIPIENT', 'faultNotificationRecipientName', ColumnVisibility.optional),
      this._factory.create('PROPERTY.FAULT_NOTIFICATION_EXECUTOR_GROUP', 'faultNotificationExecutorGroupName', ColumnVisibility.optional),
      this._factory.create(
        'PROPERTY.FAULT_NOTIFICATION_EXECUTOR_WEB_USER',
        'faultNotificationExecutorWebUserName',
        ColumnVisibility.optional
      ),
      this._factory.create(
        'PROPERTY.DWELLING_INSPECTION_EXECUTOR_WEB_USER',
        'dwellingInspectionExecutorWebUserName',
        ColumnVisibility.optional
      ),
      this._factory.createComponentBased(
        'COMMON.DOCUMENT',
        'documentInformationNotes',
        PropertyDocumentInformationNoteComponent,
        ColumnVisibility.optional
      ),
      this._factory.create('AREA.BRA', 'bra', ColumnVisibility.optional),
      this._factory.create('AREA.BOA', 'boa', ColumnVisibility.optional),
      this._factory.create('AREA.LOA', 'loa', ColumnVisibility.optional),
      this._factory.create('AREA.BIA', 'bia', ColumnVisibility.optional),
      this._factory.create('AREA.BTA', 'bta', ColumnVisibility.optional),
      this._factory.create('AREA.OVA', 'ova', ColumnVisibility.optional),
      this._factory.create('AREA.ATEMP', 'atemp', ColumnVisibility.optional),
      this._factory.create('AREA.AGAR', 'agar', ColumnVisibility.optional),
      this._factory.create('AREA.NTA', 'nta', ColumnVisibility.optional),
    ];
    for (let month = 4; month >= 0; month--) {
      let dateMonth = LocalDate.fromDate(LocalDate.today().addMonths(-month));
      columns.push(
        this._factory.create(
          getTranslation(tran, 'CONSUMPTION.CONSUMPTION') +
            ' ' +
            getTranslation(tran, 'CONSUMPTION.MEDIA_TYPE.TYPES.COLD_WATER') +
            ' ' +
            getTranslation(tran, getMonthName(dateMonth.month)) +
            ' ' +
            dateMonth.year.toString(),
          'coldWaterConsumptionM' + month.toString(),
          ColumnVisibility.optional
        )
      );
      columns.push(
        this._factory.create(
          getTranslation(tran, 'CONSUMPTION.CONSUMPTION') +
            ' ' +
            getTranslation(tran, 'CONSUMPTION.MEDIA_TYPE.TYPES.HOT_WATER') +
            ' ' +
            getTranslation(tran, getMonthName(dateMonth.month)) +
            ' ' +
            dateMonth.year.toString(),
          'hotWaterConsumptionM' + month.toString(),
          ColumnVisibility.optional
        )
      );
      columns.push(
        this._factory.create(
          getTranslation(tran, 'CONSUMPTION.CONSUMPTION') +
            ' ' +
            getTranslation(tran, 'CONSUMPTION.MEDIA_TYPE.TYPES.HEATING') +
            ' ' +
            getTranslation(tran, getMonthName(dateMonth.month)) +
            ' ' +
            dateMonth.year.toString(),
          'heatingConsumptionM' + month.toString(),
          ColumnVisibility.optional
        )
      );
      columns.push(
        this._factory.create(
          getTranslation(tran, 'CONSUMPTION.CONSUMPTION') +
            ' ' +
            getTranslation(tran, 'CONSUMPTION.MEDIA_TYPE.TYPES.ELECTRICITY') +
            ' ' +
            getTranslation(tran, getMonthName(dateMonth.month)) +
            ' ' +
            dateMonth.year.toString(),
          'electricityConsumptionM' + month.toString(),
          ColumnVisibility.optional
        )
      );
    }

    return columns;
  }

  public getRentalObjectOtherColumnDefinition(allowSort: AllowSort) {
    let columns = [
      this._factory.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
      this._factory.create('RENTAL_OBJECT_LIST.STREET_COLUMN', 'address'),
      this._factory.create('COMMON.COMPANY_NUMBER', 'companyNumber', { allowSort: allowSort, visibility: ColumnVisibility.optional }),
      this._factory.create('COMMON.COMPANY', 'companyName', ColumnVisibility.optional),
      this._factory.create('COMMON.PROPERTY', 'propertyName', ColumnVisibility.optional),
      this._factory.createNumber('RENTAL_OBJECT_LIST.SURFACE_COLUMN', 'surface', { visibility: ColumnVisibility.default, totalSum: true }),
      this._factory.create('RENTAL_OBJECT_LIST.RENTAL_OBJECT_TYPE_COLUMN', 'rentalObjectType'),
      this._factory.createDate('RENTAL_OBJECT.BLOCKED_FROM', 'blockedFrom', ColumnVisibility.optional),
      this._factory.createDate('RENTAL_OBJECT.BLOCKED_TO', 'blockedTo', ColumnVisibility.optional),
      this._factory.create('RENTAL_OBJECT.BLOCKING_REASON', 'blockingReason', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE', 'tenantOneName'),
      this._factory.createPhoneNumber('LEASE_AGREEMENT.TENANT_ONE_PHONE', 'tenantOnePhone', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_EMAIL', 'tenantOneEmail', ColumnVisibility.optional),
      this._factory.createDate('COMMON.AGREEMENT_START_DATE', 'leaseAgreementFromDate', ColumnVisibility.optional),
      this._factory.createDate('COMMON.AGREEMENT_END_DATE', 'leaseAgreementLastBillingDate', ColumnVisibility.optional),
      this._factory.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_COLUMN', 'monthlyRent', {
        allowSort: allowSort,
      }),
      this._factory.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_COLUMN_LEASE_AGREEMENT', 'currentMonthRentLeaseAgreement', {
        visibility: ColumnVisibility.optional,
        allowSort: allowSort,
      }),
      this._factory.createMoney('RENTAL_OBJECT_LIST.RENT_LOSS_MONTH_CURRENT_COLUMN', 'rentLossCurrentMonth', {
        visibility: ColumnVisibility.optional,
        allowSort: allowSort,
      }),
      this._factory.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_NEXT_COLUMN', 'monthlyRentNext', {
        visibility: ColumnVisibility.optional,
        allowSort: allowSort,
      }),
      this._factory.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_NEXT_COLUMN_LEASE_AGREEMENT', 'nextMonthRentLeaseAgreement', {
        visibility: ColumnVisibility.optional,
        allowSort: allowSort,
      }),
      this._factory.createMoney('RENTAL_OBJECT_LIST.RENT_LOSS_MONTH_NEXT_COLUMN', 'rentLossNextMonth', {
        visibility: ColumnVisibility.optional,
        allowSort: allowSort,
      }),
      this._factory.createMoney('COMMON.YEAR_RENT', 'yearRent', {
        allowSort: allowSort,
      }),
      this._factory.createMoney('COMMON.YEAR_RENT_PER_SQM', 'yearRentPerSqm', {
        visibility: ColumnVisibility.optional,
        allowSort: allowSort,
      }),
      this._factory.createMoney('COMMON.YEAR_RENT_INCL_VAT', 'yearRentIncludingVat', {
        visibility: ColumnVisibility.optional,
        allowSort: allowSort,
      }),
      this._factory.createMoney('COMMON.RENT_LOSS_THIS_YEAR', 'rentLossCurrentYear', {
        visibility: ColumnVisibility.optional,
        allowSort: allowSort,
      }),
      this._factory.createMoney('COMMON.RENT_LOSS_PREVIOUS_YEAR', 'rentLossPreviousYear', {
        visibility: ColumnVisibility.optional,
        allowSort: allowSort,
      }),
    ];
    return columns;
  }

  private getRentalObjectPremisesColumnDefinition() {
    let columns = [
      this._factory.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
      this._factory.create('RENTAL_OBJECT_LIST.STREET_COLUMN', 'address'),
      this._factory.create('COMMON.COMPANY_NUMBER', 'companyNumber', ColumnVisibility.optional),
      this._factory.create('COMMON.COMPANY', 'companyName', ColumnVisibility.optional),
      this._factory.create('COMMON.PROPERTY', 'propertyName', ColumnVisibility.optional),
      this._factory.create('COMMON.BUILDING_NUMBER', 'buildingNumber', ColumnVisibility.optional),
      this._factory.create('COMMON.BUILDING', 'buildingName', ColumnVisibility.optional),
      this._factory.createNumber('RENTAL_OBJECT_LIST.SURFACE_COLUMN', 'surface', { visibility: ColumnVisibility.default, totalSum: true }),
      this._factory.create('DWELLING_FORM.FLOOR_LABEL', 'floor'),
      this._factory.create('RENTAL_OBJECT_TYPE_FORM.NUMBER_OF_ROOMS_LABEL', 'numberOfRooms', ColumnVisibility.optional),
      this._factory.create('RENTAL_OBJECT_LIST.RENTAL_OBJECT_TYPE_COLUMN', 'rentalObjectType'),
      this._factory.createDate('RENTAL_OBJECT.BLOCKED_FROM', 'blockedFrom', ColumnVisibility.optional),
      this._factory.createDate('RENTAL_OBJECT.BLOCKED_TO', 'blockedTo', ColumnVisibility.optional),
      this._factory.create('RENTAL_OBJECT.BLOCKING_REASON', 'blockingReason', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE', 'tenantOneName'),
      this._factory.createPhoneNumber('LEASE_AGREEMENT.TENANT_ONE_PHONE', 'tenantOnePhone', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_EMAIL', 'tenantOneEmail', ColumnVisibility.optional),
      this._factory.createDate('COMMON.AGREEMENT_START_DATE', 'leaseAgreementFromDate', ColumnVisibility.optional),
      this._factory.createDate('COMMON.AGREEMENT_END_DATE', 'leaseAgreementLastBillingDate', ColumnVisibility.optional),
      this._factory.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_COLUMN', 'monthlyRent'),
      this._factory.createMoney(
        'RENTAL_OBJECT_LIST.MOUNTHLY_RENT_COLUMN_LEASE_AGREEMENT',
        'currentMonthRentLeaseAgreement',
        ColumnVisibility.optional
      ),
      this._factory.createMoney('RENTAL_OBJECT_LIST.RENT_LOSS_MONTH_CURRENT_COLUMN', 'rentLossCurrentMonth', ColumnVisibility.optional),
      this._factory.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_NEXT_COLUMN', 'monthlyRentNext', ColumnVisibility.optional),
      this._factory.createMoney(
        'RENTAL_OBJECT_LIST.MOUNTHLY_RENT_NEXT_COLUMN_LEASE_AGREEMENT',
        'nextMonthRentLeaseAgreement',
        ColumnVisibility.optional
      ),
      this._factory.createMoney('RENTAL_OBJECT_LIST.RENT_LOSS_MONTH_NEXT_COLUMN', 'rentLossNextMonth', ColumnVisibility.optional),
      this._factory.createMoney('COMMON.YEAR_RENT', 'yearRent'),
      this._factory.createMoney('COMMON.YEAR_RENT_PER_SQM', 'yearRentPerSqm', ColumnVisibility.optional),
      this._factory.createMoney('COMMON.YEAR_RENT_INCL_VAT', 'yearRentIncludingVat'),
      this._factory.create('DWELLING_FORM.INDIVIDUAL_METER_ELECTRICITY_LABEL', 'indvidualMeterElectricityText', ColumnVisibility.optional),
      this._factory.create('DWELLING_FORM.INDIVIDUAL_METER_WATER_LABEL', 'indvidualMeterWaterText', ColumnVisibility.optional),
      this._factory.create('DWELLING_FORM.INDIVIDUAL_METER_COLD_WATER_LABEL', 'indvidualMeterColdWaterText', ColumnVisibility.optional),
      this._factory.create('DWELLING_FORM.INDIVIDUAL_METER_HEATING_LABEL', 'indvidualMeterHeatingText', ColumnVisibility.optional),
    ];
    return columns;
  }

  private getRentalObjectTypeColumnDefinition() {
    let columns = [
      this._factory.create('RENTAL_OBJECT_TYPE_LIST.NAME_COLUMN', 'name'),
      this._factory.createTranslation('RENTAL_OBJECT_TYPE_LIST.CATEGORY_COLUMN', 'categoryCaption'),
      this._factory.create('RENTAL_OBJECT_TYPE_FORM.NUMBER_OF_ROOMS_LABEL', 'numberOfRooms', ColumnVisibility.optional),
      this._factory.createBoolean(
        'RENTAL_OBJECT_TYPE_FORM.PARKINGHASELECTRICITY_LABEL',
        'parkingHasElectricity',
        ColumnVisibility.optional
      ),
      this._factory.createBoolean('RENTAL_OBJECT_TYPE_FORM.PARKINGHASROOF_LABEL', 'parkingHasRoof', ColumnVisibility.optional),
      this._factory.createBoolean('RENTAL_OBJECT_TYPE_FORM.ISGARAGE_LABEL', 'isGarage', ColumnVisibility.optional),
      this._factory.create('RENTAL_OBJECT_TYPE_FORM.RH91', 'rH91', ColumnVisibility.optional),
      this._factory.create('RENTAL_OBJECT_TYPE_FORM.BVK91', 'pointsBvk91', ColumnVisibility.optional),
    ];
    return columns;
  }

  private getSubleaseRequestColumnDefinition() {
    let columns = [
      // SubleaseTODO
      //    this._factory.createTranslation('SUBLEASE_REQUEST.DECISION', 'decisionText', ColumnVisibility.mandatory),
      this._factory.createDate('SUBLEASE_REQUEST.FROMDATE', 'fromDate', ColumnVisibility.mandatory),
      this._factory.createDate('SUBLEASE_REQUEST.TODATE', 'toDate', ColumnVisibility.mandatory),
      this._factory.create('SUBLEASE_REQUEST.SUBLEASEE_NAME', 'subleaseeName', ColumnVisibility.default),
      this._factory.create('SUBLEASE_REQUEST.SUBLEASEE_EMAIL', 'subleaseeEmail', ColumnVisibility.optional),
      this._factory.create('SUBLEASE_REQUEST.SUBLEASEE_PHONE', 'subleaseePhone', ColumnVisibility.optional),
      this._factory.createDate('SUBLEASE_REQUEST.CREATED_DATE', 'created', ColumnVisibility.optional),
      //      this._factory.createDate('SUBLEASE_REQUEST.DECISION_DATE', 'decisionDate', ColumnVisibility.optional)
    ];
    return columns;
  }

  private getKeyTypeColumnDefinition() {
    let columns = [
      this._factory.create('KEYTYPE.NAME', 'name', ColumnVisibility.mandatory),
      this._factory.createMoney('KEYTYPE.KEY_PRICE', 'keyPrice', { totalSum: false, visibility: ColumnVisibility.mandatory }),
    ];
    return columns;
  }

  getImdImportColumnDefinition(): ColumnDefinition[] {
    let columns = [
      this._factory.create('CONSUMPTION.IMPORT.IMPORT_IDENTIFIER', 'importIdentifier', ColumnVisibility.mandatory),
      this._factory.createDateTime('CONSUMPTION.IMPORT.RECIEVED_AT', 'recievedAt', ColumnVisibility.default),
      this._factory.createDateTime('CONSUMPTION.IMPORT.IMPORTED_AT', 'importedAt', ColumnVisibility.default),
    ];
    return columns;
  }

  getImdPriceListColumnDefinition(allowSort: AllowSort): ColumnDefinition[] {
    let columns = [
      this._factory.create('CONSUMPTION.PRICELIST.NAME', 'name', ColumnVisibility.mandatory),
      this._factory.create('CONSUMPTION.MEDIA_TYPE.TYPE', 'meterTypeCaption', ColumnVisibility.mandatory),
      this._factory.create('LEASE_AGREEMENT_ROW.ARTICLE', 'articleCodeCaption', ColumnVisibility.default),
      this._factory.createMoney('CONSUMPTION.PRICELIST.TODAY_PRICE', 'todayPrice', ColumnVisibility.default),
      this._factory.createMoney('CONSUMPTION.PRICELIST.LATEST_PRICE', 'latestPrice', ColumnVisibility.default),
    ];
    return columns;
  }

  private getInvoiceRowGroupColumnDefinition() {
    let columns = [this._factory.create('ARTICLE_GROUP.NAME', 'caption', ColumnVisibility.mandatory)];
    return columns;
  }

  getLocationAccessGroupColumnDefinition(): ColumnDefinition[] {
    let columns = [this._factory.create('LOCATION_ACCESS.GROUP_NAME', 'name', ColumnVisibility.mandatory)];
    return columns;
  }

  getRentalObjectFaultNotificationsColumnDefinition(): ColumnDefinition[] {
    let columns = [
      this._factory.create('FAULT_NOTIFICATION.NUMBER', 'number', ColumnVisibility.optional),
      this._factory.createTranslation('FAULT_NOTIFICATION.STATUS', 'faultNotificationStatusText'),
      this._factory.createDateTime('FAULT_NOTIFICATION.CREATED_DATE', 'createdDate'),
      this._factory.create('COMMON.DESCRIPTION', 'firstPartOfDescription'),
      this._factory.create('FAULT_NOTIFICATION.CATEGORY', 'category'),
      this._factory.create('FAULT_NOTIFICATION.SUBCATEGORY', 'subcategory'),
      this._factory.createTranslation('FAULT_NOTIFICATION.PRIORITY', 'priorityCaption'),
      this._factory.create('FAULT_NOTIFICATION.RESOURCE', 'resourceName', ColumnVisibility.optional),
      this._factory.create('FAULT_NOTIFICATION.TENANT', 'tenant', ColumnVisibility.optional),
      this._factory.createPhoneNumber('FAULT_NOTIFICATION.TENANT_PHONE', 'tenantPhone', ColumnVisibility.optional),
      this._factory.create('FAULT_NOTIFICATION.TENANT_EMAIL', 'tenantEmail', ColumnVisibility.optional),
      this._factory.create('FAULT_NOTIFICATION.CREATOR_NAME', 'creatorName', ColumnVisibility.optional),
      this._factory.create('FAULT_NOTIFICATION.CREATOR_USERNAME', 'creatorUsername', ColumnVisibility.optional),
    ];

    return columns;
  }

  getImdMeterModelColumnDefinition(allowSort: AllowSort): ColumnDefinition[] {
    let columns = [
      this._factory.create('CONSUMPTION.METER_MODEL.METER_MODEL', 'name', ColumnVisibility.mandatory),
      this._factory.create('CONSUMPTION.MEDIA_TYPE.TYPE', 'meterTypeCaption', ColumnVisibility.default),
      this._factory.create('CONSUMPTION.METER_MODEL.MANUFACTURER', 'manufacturer', ColumnVisibility.default),
    ];
    return columns;
  }

  getRentalObjectKeyColumnDefinition(): ColumnDefinition<any, any, any>[] {
    return [
      this._factory.create('COMMON.COMPANY', 'companyName', ColumnVisibility.optional),
      this._factory.create('COMMON.PROPERTY', 'propertyName', ColumnVisibility.optional),
      this._factory.create('RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL', 'rentalId', ColumnVisibility.mandatory),
      this._factory.create('KEYTYPE.NAME', 'keyTypeName', ColumnVisibility.default),
      this._factory.create('RENTALOBJECT_KEY.KEY', 'key', ColumnVisibility.default),
      this._factory.create('RENTALOBJECT_KEY.CAPTION', 'caption', ColumnVisibility.default),
      this._factory.create('RENTALOBJECT_KEY.QUANTITY', 'quantity', ColumnVisibility.default),
      this._factory.create('RENTALOBJECT_KEY.QUANTITY_LENT_OUT', 'quantityLentOut', ColumnVisibility.default),
      this._factory.create('RENTALOBJECT_KEY.SYSTEM_NUMBER', 'systemNumber', ColumnVisibility.optional),
      this._factory.create('RENTALOBJECT_KEY.MARKING', 'marking', ColumnVisibility.default),
      this._factory.create('RENTALOBJECT_KEY.SUPPLIER', 'supplier', ColumnVisibility.default),
    ];
  }

  getMortgagePrescriptionColumnDefinition(): ColumnDefinition<any, any, any>[] {
    return [
      this._factory.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
      this._factory.create('COMMON.COMPANY', 'companyNumberAndName'),
      this._factory.create('MORTGAGE.CREDIT_INSTITUTE', 'creditInstituteName'),
      this._factory.create('COOPERATIVE_HOUSING.MORTGAGE_ORDER', 'mortgageOrder'),
      this._factory.create('COOPERATIVE_HOUSING.MORTGAGE_PRESCRIPTION_NUMBER', 'mortgageNumber'),
      this._factory.createDate('COOPERATIVE_HOUSING.MORTGAGE_FROM', 'fromDate'),
      this._factory.createDate('COOPERATIVE_HOUSING.MORTGAGE_TO', 'toDate'),
      this._factory.createMoney('COOPERATIVE_HOUSING.MORTGAGE_AMOUNT', 'amount'),
    ];
  }

  getBankTransactionImportColumnDefinition(): ColumnDefinition<any, any, any>[] {
    return [
      this._factory.create('COMPANY.BANK_ACCOUNT.IBAN', 'bankAccountNumber', ColumnVisibility.default),
      this._factory.create('COMMON.COMPANY', 'companyName'),
      this._factory.createMoney('BANK_IMPORT_TRANSACTIONS.AMOUNT', 'amount', ColumnVisibility.optional),
      this._factory.create('BANK_IMPORT_TRANSACTIONS.DEBTORNAME', 'debtorName', ColumnVisibility.default),
      this._factory.create('BANK_IMPORT_TRANSACTIONS.TRANSACTION_TEXT', 'transactionText', ColumnVisibility.optional),
      this._factory.createDateTime('BANK_IMPORT_TRANSACTIONS.IMPORTED_AT', 'importedAt', ColumnVisibility.default),
      this._factory.createDate('BANK_IMPORT_TRANSACTIONS.TRANSACTION_DATE', 'transactionDate', ColumnVisibility.default),
      this._factory.createTranslation('BANK_IMPORT_TRANSACTIONS.TRANSACTION_STATUS', 'transactionStatusCaption'),
      this._factory.createBoolean('BANK_IMPORT_TRANSACTIONS.IS_AUTO_GIRO', 'isAutoGiro', ColumnVisibility.optional),
    ];
  }

  getTenantColumnDefinition(allowSort: AllowSort): ColumnDefinition[] {
    let columns = [
      this._factory.create('TENANT.CONTACT_NUMBER', 'number'),
      this._factory.create('TENANT.NAME', 'name'),
      this._factory.create('TENANT.REFERENCE_NAME', 'referenceName', ColumnVisibility.optional),
      this._factory.create('TENANT.CIVIC_ORG_NUMBER', 'contactIdentification', ColumnVisibility.optional),
      this._factory.create('COMPANY.VAT_NUMBER', 'vatNumber', ColumnVisibility.optional),
      this._factory.create('TENANT.EMAIL', 'email'),
      this._factory.create('COMMON.EMAIL_INVOICING', 'emailInvoicing', ColumnVisibility.optional),
      this._factory.createPhoneNumber('TENANT.PHONE', 'phone'),
      this._factory.create('COMMON.INVOICE_ADDRESS', 'invoiceAddressString', {
        visibility: ColumnVisibility.optional,
        allowSort: allowSort,
        serverSideSortBinding: 'number', // TODO: <-- this cannot be correct...
      }),
      this._factory.create('TENANT.EINVOICE_ADDRESS', 'eInvoiceAddress', {
        visibility: ColumnVisibility.optional,
        allowSort: allowSort,
        serverSideSortBinding: 'number', // TODO: <-- this cannot be correct...
      }),
      this._factory.create('TENANT.USER_ACCOUNT', 'userAccountText', {
        allowSort: allowSort,
        serverSideSortBinding: 'webUserId',
      }),
      this._factory.createBoolean('TENANT.NO_JOINT_DEBITING', 'noJointDebiting'),
      this._factory.create('COMMON.NOTE', 'firstPartOfNote', {
        allowSort: allowSort,
        serverSideSortBinding: 'number', // TODO: <-- this cannot be correct...
      }),
      this._factory.createComponentBased(
        'COMMON.DOCUMENT',
        'documentInformationNotes',
        ContactDocumentInformationNoteComponent,
        ColumnVisibility.optional
      ),
    ];
    return columns;
  }

  private getRentApplicationColumnDefinition() {
    let columns = [
      this._factory.createTranslation('RENT_APPLICATION.OBJECT_CATEGORY', 'rentalObjectCategoryCaption', ColumnVisibility.default),
      this._factory.create('TENANT.CONTACT_NUMBER', 'contactCustomerNumber', ColumnVisibility.optional),
      this._factory.create('TENANT.NAME', 'contactName', ColumnVisibility.mandatory),
      this._factory.createDate('RENT_APPLICATION.REGISTRATION_DATE', 'applicationDate', ColumnVisibility.default),
      this._factory.create('TENANT.CIVIC_ORG_NUMBER', 'contactIdentification', ColumnVisibility.optional),
      this._factory.create('TENANT.EMAIL', 'contactEmail', ColumnVisibility.default),
      this._factory.createPhoneNumber('TENANT.PHONE', 'contactPhone', ColumnVisibility.default),
      this._factory.create('RENT_APPLICATION.MIN_ROOMS', 'numberOfRoomsMin', ColumnVisibility.optional),
      this._factory.create('RENT_APPLICATION.MAX_ROOMS', 'numberOfRoomsMax', ColumnVisibility.optional),
      this._factory.create('COMMON.NOTE', 'firstPartOfNote', ColumnVisibility.default),
    ];
    return columns;
  }

  private getRentLossColumnDefinition() {
    return [
      this._factory.create('COMMON.COMPANY', 'companyName', ColumnVisibility.mandatory),
      this._factory.create('COMMON.PROPERTY', 'propertyCaption', ColumnVisibility.mandatory),
      this._factory.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId', ColumnVisibility.default),
      this._factory.createNumber('RENT_LOSS.VACANCY_DAYS', 'vacancyDays', ColumnVisibility.default),
      this._factory.createMoney('COMMON.AMOUNT', 'amount', ColumnVisibility.mandatory),
    ];
  }

  private getVacantRentalObjectDwellingColumnDefinition() {
    let columns = [
      this._factory.create('VACANT_RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
      this._factory.create('VACANT_RENTAL_OBJECT_LIST.STREET_COLUMN', 'address'),
      this._factory.create('COMMON.COMPANY_NUMBER', 'companyNumber', ColumnVisibility.optional),
      this._factory.create('COMMON.COMPANY', 'companyName', ColumnVisibility.optional),
      this._factory.create('COMMON.PROPERTY', 'propertyName', ColumnVisibility.optional),
      this._factory.createNumber('VACANT_RENTAL_OBJECT_LIST.SURFACE_COLUMN', 'surface', {
        visibility: ColumnVisibility.default,
        totalSum: true,
      }),
      this._factory.create('VACANT_RENTAL_OBJECT_LIST.RENTAL_OBJECT_TYPE_COLUMN', 'rentalObjectType'),
      this._factory.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_FROM_COLUMN', 'vacantFrom'),
      this._factory.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_TO_COLUMN', 'vacantTo'),
      this._factory.createDate('TERMINATE_LEASE_AGREEMENT_FORM.DESIRED_REMOVAL_LABEL', 'desiredMoveOutDate', ColumnVisibility.optional),
      this._factory.createDate('RENTAL_OBJECT.BLOCKED_FROM', 'blockedFrom', ColumnVisibility.optional),
      this._factory.createDate('RENTAL_OBJECT.BLOCKED_TO', 'blockedTo', ColumnVisibility.optional),
      this._factory.create('RENTAL_OBJECT.BLOCKING_REASON', 'blockingReason', ColumnVisibility.optional),
      this._factory.create('COMMON.MOVING_OUT_TENANT_NAME', 'movingOutTenantName'),
      this._factory.createPhoneNumber('COMMON.MOVING_OUT_TENANT_PHONE', 'movingOutTenantPhone', ColumnVisibility.optional),
      this._factory.create('COMMON.MOVING_OUT_TENANT_EMAIL', 'movingOutTenantEmail', ColumnVisibility.optional),
      this._factory.createDate('COMMON.AGREEMENT_START_DATE', 'leaseAgreementFromDate', ColumnVisibility.optional),
      this._factory.createDate('COMMON.AGREEMENT_END_DATE', 'leaseAgreementLastBillingDate', ColumnVisibility.optional),
      this._factory.createMoney('VACANT_RENTAL_OBJECT_LIST.MOUNTHLY_RENT_COLUMN', 'monthlyRent'),
      this._factory.createMoney('COMMON.YEAR_RENT', 'yearRent'),
      this._factory.createMoney('COMMON.YEAR_RENT_PER_SQM', 'yearRentPerSqm', ColumnVisibility.optional),
      this._factory.createMoney('RENTAL_OBJECT_LIST.NEGOTIATION_YEAR_RENT', 'negotiationYearRent', ColumnVisibility.optional),
      this._factory.createMoney('RENTAL_OBJECT_LIST.NEGOTIATION_YEAR_RENT_PER_SQM', 'negotiationYearRentPerSqm', ColumnVisibility.optional),
      this._factory.createMoney('COMMON.NORMRENT', 'normRentRH91', ColumnVisibility.optional),
      this._factory.createMoney('RENTAL_OBJECT_LIST.BVK91RENT', 'rentBVK91', ColumnVisibility.optional),
      this._factory.createBoolean('DWELLING.FURNISHED', 'furnished', ColumnVisibility.optional),
      this._factory.create('COMMON.NOTE', 'firstPartOfNote', ColumnVisibility.optional),
    ];
    return columns;
  }

  private getVacantRentalObjectParkingColumnDefinition() {
    let columns = [
      this._factory.create('VACANT_RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
      this._factory.create('VACANT_RENTAL_OBJECT_LIST.STREET_COLUMN', 'address'),
      this._factory.create('RENTAL_OBJECT_LIST.PARKING_NUMBER_COLUMN', 'parkingNumber', ColumnVisibility.optional),
      this._factory.create('COMMON.COMPANY_NUMBER', 'companyNumber', ColumnVisibility.optional),
      this._factory.create('COMMON.COMPANY', 'companyName', ColumnVisibility.optional),
      this._factory.create('COMMON.PROPERTY', 'propertyName', ColumnVisibility.optional),
      this._factory.create('VACANT_RENTAL_OBJECT_LIST.RENTAL_OBJECT_TYPE_COLUMN', 'rentalObjectType'),
      this._factory.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_FROM_COLUMN', 'vacantFrom'),
      this._factory.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_TO_COLUMN', 'vacantTo'),
      this._factory.createDate('RENTAL_OBJECT.BLOCKED_FROM', 'blockedFrom', ColumnVisibility.optional),
      this._factory.createDate('RENTAL_OBJECT.BLOCKED_TO', 'blockedTo', ColumnVisibility.optional),
      this._factory.create('RENTAL_OBJECT.BLOCKING_REASON', 'blockingReason', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE', 'movingOutTenantName'),
      this._factory.createPhoneNumber('LEASE_AGREEMENT.TENANT_ONE_PHONE', 'movingOutTenantPhone', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_EMAIL', 'movingOutTenantEmail', ColumnVisibility.optional),
      this._factory.createDate('COMMON.AGREEMENT_START_DATE', 'leaseAgreementFromDate', ColumnVisibility.optional),
      this._factory.createDate('COMMON.AGREEMENT_END_DATE', 'leaseAgreementLastBillingDate', ColumnVisibility.optional),
      this._factory.createMoney('VACANT_RENTAL_OBJECT_LIST.MOUNTHLY_RENT_COLUMN', 'monthlyRent'),
      this._factory.createMoney('COMMON.YEAR_RENT', 'yearRent'),
      this._factory.create('COMMON.NOTE', 'firstPartOfNote', ColumnVisibility.optional),
    ];
    return columns;
  }

  private getVacantRentalObjectColumnDefinition() {
    let columns = [
      this._factory.create('VACANT_RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
      this._factory.create('VACANT_RENTAL_OBJECT_LIST.STREET_COLUMN', 'address'),
      this._factory.create('COMMON.COMPANY_NUMBER', 'companyNumber', ColumnVisibility.optional),
      this._factory.create('COMMON.COMPANY', 'companyName', ColumnVisibility.optional),
      this._factory.create('COMMON.PROPERTY', 'propertyName', ColumnVisibility.optional),
      this._factory.createNumber('VACANT_RENTAL_OBJECT_LIST.SURFACE_COLUMN', 'surface', {
        visibility: ColumnVisibility.default,
        totalSum: true,
      }),
      this._factory.create('VACANT_RENTAL_OBJECT_LIST.RENTAL_OBJECT_TYPE_COLUMN', 'rentalObjectType'),
      this._factory.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_FROM_COLUMN', 'vacantFrom'),
      this._factory.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_TO_COLUMN', 'vacantTo'),
      this._factory.createDate('RENTAL_OBJECT.BLOCKED_FROM', 'blockedFrom', ColumnVisibility.optional),
      this._factory.createDate('RENTAL_OBJECT.BLOCKED_TO', 'blockedTo', ColumnVisibility.optional),
      this._factory.create('RENTAL_OBJECT.BLOCKING_REASON', 'blockingReason', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE', 'movingOutTenantName'),
      this._factory.createPhoneNumber('LEASE_AGREEMENT.TENANT_ONE_PHONE', 'movingOutTenantPhone', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.TENANT_ONE_EMAIL', 'movingOutTenantEmail', ColumnVisibility.optional),
      this._factory.createDate('COMMON.AGREEMENT_START_DATE', 'leaseAgreementFromDate', ColumnVisibility.optional),
      this._factory.createDate('COMMON.AGREEMENT_END_DATE', 'leaseAgreementLastBillingDate', ColumnVisibility.optional),
      this._factory.createMoney('VACANT_RENTAL_OBJECT_LIST.MOUNTHLY_RENT_COLUMN', 'monthlyRent'),
      this._factory.createMoney('COMMON.YEAR_RENT', 'yearRent'),
      this._factory.createMoney('COMMON.YEAR_RENT_PER_SQM', 'yearRentPerSqm', ColumnVisibility.optional),
      this._factory.createMoney('COMMON.YEAR_RENT_INCL_VAT', 'yearRentIncludingVat', ColumnVisibility.optional),
      this._factory.create('COMMON.NOTE', 'firstPartOfNote', ColumnVisibility.optional),
    ];
    return columns;
  }

  private getVacantRentalObjectPremisesColumnDefinition() {
    let columns = [
      this._factory.create('VACANT_RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
      this._factory.create('VACANT_RENTAL_OBJECT_LIST.STREET_COLUMN', 'address'),
      this._factory.create('COMMON.COMPANY_NUMBER', 'companyNumber', ColumnVisibility.optional),
      this._factory.create('COMMON.COMPANY', 'companyName', ColumnVisibility.optional),
      this._factory.create('COMMON.PROPERTY', 'propertyName', ColumnVisibility.optional),
      this._factory.createNumber('VACANT_RENTAL_OBJECT_LIST.SURFACE_COLUMN', 'surface', {
        visibility: ColumnVisibility.default,
        totalSum: true,
      }),
      this._factory.create('VACANT_RENTAL_OBJECT_LIST.RENTAL_OBJECT_TYPE_COLUMN', 'rentalObjectType'),
      this._factory.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_FROM_COLUMN', 'vacantFrom'),
      this._factory.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_TO_COLUMN', 'vacantTo'),
      this._factory.createDate('TERMINATE_LEASE_AGREEMENT_FORM.DESIRED_REMOVAL_LABEL', 'desiredMoveOutDate', ColumnVisibility.optional),
      this._factory.createDate('RENTAL_OBJECT.BLOCKED_FROM', 'blockedFrom'),
      this._factory.createDate('RENTAL_OBJECT.BLOCKED_TO', 'blockedTo'),
      this._factory.create('RENTAL_OBJECT.BLOCKING_REASON', 'blockingReason', ColumnVisibility.optional),
      this._factory.createMoney('VACANT_RENTAL_OBJECT_LIST.MOUNTHLY_RENT_COLUMN', 'monthlyRent', ColumnVisibility.optional),
      this._factory.createMoney('COMMON.YEAR_RENT', 'yearRent'),
      this._factory.createMoney('COMMON.YEAR_RENT_PER_SQM', 'yearRentPerSqm', ColumnVisibility.optional),
      this._factory.createMoney('COMMON.YEAR_RENT_INCL_VAT', 'yearRentIncludingVat', ColumnVisibility.optional),
      this._factory.create('COMMON.NOTE', 'firstPartOfNote', ColumnVisibility.optional),
    ];
    return columns;
  }

  public getOtherLeaseAgreementsColumnDefintion(): ColumnDefinition[] {
    return [
      this._factory.createTranslation('LEASE_AGREEMENT.STATUS', 'statusCaption'),
      this._factory.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber'),
      this._factory.create('LEASE_AGREEMENT.POPULAR_NAME', 'popularName'),
      this._factory.createDate('LEASE_AGREEMENT.FROM_DATE', 'fromDate'),
      this._factory.createDate('LEASE_AGREEMENT.TO_DATE', 'lastBillingDate'),
      this._factory.create('RENTAL_OBJECT.OBJECT', 'rentalObject'),
      this._factory.create('LEASE_AGREEMENT.RENTAL_OBJECT_TYPE', 'rentalObjectType'),
      this._factory.create('LEASE_AGREEMENT.CONTRACT_HOLDER_ONE', 'tenantOne', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.CONTRACT_HOLDER_TWO', 'tenantTwoName', ColumnVisibility.optional),
      this._factory.create('COMMON.COMPANY_NUMBER', 'companyNumber', ColumnVisibility.optional),
      this._factory.create('COMMON.COMPANY', 'companyName', ColumnVisibility.optional),
      this._factory.create('PROPERTY.NUMBER', 'propertyNumber', ColumnVisibility.optional),
      this._factory.create('COMMON.PROPERTY', 'propertyName', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT.INVOICE_DELIVERY_METHOD', 'invoiceDeliveryMethodText', ColumnVisibility.optional),
      this._factory.create('INVOICE.PREVIEW.INVOICE_ADDRESS', 'invoiceAddressString', ColumnVisibility.optional),
      this._factory.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT_CURRENT', 'currentPeriodRentAmount', ColumnVisibility.optional),
      this._factory.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT_NEXT', 'nextPeriodRentAmount'),
      this._factory.create('LEASE_AGREEMENT.INVOICE_PERIOD', 'invoicePeriodText', ColumnVisibility.optional),
    ];
  }

  getLeaseAgreementfaultNotificationColumnDefinition(): ColumnDefinition[] {
    return [
      this._factory.createTranslation('FAULT_NOTIFICATION.STATUS', 'statusCaption'),
      this._factory.createDateTime('FAULT_NOTIFICATION.CREATED_DATE', 'createdDate'),
      this._factory.create('COMMON.DESCRIPTION', 'firstPartOfDescription'),
      this._factory.create('FAULT_NOTIFICATION.CATEGORY', 'category'),
      this._factory.create('FAULT_NOTIFICATION.SUBCATEGORY', 'subcategory'),
      this._factory.createTranslation('FAULT_NOTIFICATION.PRIORITY', 'priorityCaption'),
    ];
  }

  private getVacantRentalObjectGroupedColumnDefinition(tran: any): ColumnDefinition[] {
    let columns = [
      this._factory.create('COMMON.MONTH_1', 'vacantCurrentMonthCount'),
      this._factory.create('COMMON.MONTH_2', 'vacantOneMonthFromNowCount'),
      this._factory.create('COMMON.MONTH_3', 'vacantTwoMonthFromNowCount'),
      this._factory.create('COMMON.MONTH_4', 'vacantThreeMonthFromNowCount'),
      this._factory.create('COMMON.MONTH_5', 'vacantFourMonthFromNowCount'),
      this._factory.create('COMMON.MONTH_6', 'vacantFiveMonthFromNowCount'),
      this._factory.create('COMMON.LATER', 'vacantLaterCount'),
      this._factory.create('RENTAL_OBJECT.BLOCKED_NOW', 'validLettingBlockCount'),
    ];

    let month1 = columns.find(x => x.caption === 'COMMON.MONTH_1');
    if (month1) {
      month1.caption = `${getTranslation(tran, getMonthName(LocalDate.today().month))} ${LocalDate.today().year}`;
    }
    let month2 = columns.find(x => x.caption === 'COMMON.MONTH_2');
    if (month2) {
      month2.caption = `${getTranslation(tran, getMonthName(LocalDate.fromDate(LocalDate.today().addMonths(1)).month))}
      ${LocalDate.fromDate(LocalDate.today().addMonths(1)).year}`;
    }
    let month3 = columns.find(x => x.caption === 'COMMON.MONTH_3');
    if (month3) {
      month3.caption = `${getTranslation(tran, getMonthName(LocalDate.fromDate(LocalDate.today().addMonths(2)).month))}
      ${LocalDate.fromDate(LocalDate.today().addMonths(2)).year}`;
    }
    let month4 = columns.find(x => x.caption === 'COMMON.MONTH_4');
    if (month4) {
      month4.caption = `${getTranslation(tran, getMonthName(LocalDate.fromDate(LocalDate.today().addMonths(3)).month))}
      ${LocalDate.fromDate(LocalDate.today().addMonths(3)).year}`;
    }
    let month5 = columns.find(x => x.caption === 'COMMON.MONTH_5');
    if (month5) {
      month5.caption = `${getTranslation(tran, getMonthName(LocalDate.fromDate(LocalDate.today().addMonths(4)).month))}
      ${LocalDate.fromDate(LocalDate.today().addMonths(4)).year}`;
    }
    let month6 = columns.find(x => x.caption === 'COMMON.MONTH_6');
    if (month6) {
      month6.caption = `${getTranslation(tran, getMonthName(LocalDate.fromDate(LocalDate.today().addMonths(5)).month))}
      ${LocalDate.fromDate(LocalDate.today().addMonths(5)).year}`;
    }
    return columns;
  }

  createWithTranslation(modelType: string, translation: any): ColumnDefinition[] {
    switch (modelType) {
      case 'VacantRentalObjectGrouped':
        return this.getVacantRentalObjectGroupedColumnDefinition(translation);

      default:
        return null;
    }
  }

  create(modelType: string): ColumnDefinition[] {
    switch (modelType) {
      case 'Appendix':
        return this.getAppendixColumnDefinition();

      case 'Article':
        return this.getArticleColumnDefinition();

      case 'ArticleGroup':
        return this.getArticleGroupColumnDefinition();

      case 'BlockContract':
        return this.getContractBlockColumnDefinition();

      case 'Building':
        return this.getBuildingColumnDefinition();

      case 'BuildingSpaceType':
        return this.getBuildingSpaceTypeColumnDefinition();

      case 'Company':
        return this.getCompanyColumnDefinition();

      case 'ControlDataExport':
        return this.getControlDataExportColumnDefinition();

      case 'CooperativeHousingTransfer':
        return this.getCooperativeHousingTransferColumnDefinition();

      case 'CustomProperty':
        return this.getCustomPropertyColumnDefinition();

      case 'LettingFeatureAdmin':
        return this.getLettingFeatureAdminColumnDefinition();

      case 'ObjectComponentCategory':
        return this.getObjectComponentCategoryColumnDefinition();

      case 'Premises':
        return this.getRentalObjectPremisesColumnDefinition();

      case 'ParkingContract':
        return this.getContractParkingColumnDefinition();

      case 'CooperativeHousingContract':
        return this.getContractCooperativeHousingColumnDefinition();

      case 'DwellingContract':
        return this.getContractDwellingColumnDefinition();

      case 'InvoiceRowGroup':
        return this.getInvoiceRowGroupColumnDefinition();

      case 'IndexClauseSettings':
        return this.getIndexClauseSettingsColumnDefinition();

      case 'KeyType':
        return this.getKeyTypeColumnDefinition();

      case 'PremisesContract':
        return this.getContractPremisesColumnDefinition();

      case 'PriceList':
        return this.getPriceListColumnDefinition();

      case 'PriceListItem':
        return this.getPriceListItemColumnDefinition();

      case 'Log':
        return this.getLogColumnDefinition();

      case 'LeaseAgreementImdConsumption':
        return this.getLeaseAgreementImdConsumptionColumnDefinition();

      case 'OtherContract':
        return this.getContractOtherColumnDefinition();

      case 'Property':
        return this.getPropertyColumnDefinition();

      case 'RentApplication':
        return this.getRentApplicationColumnDefinition();

      case 'RentLoss':
        return this.getRentLossColumnDefinition();

      case 'RentalObjectType':
        return this.getRentalObjectTypeColumnDefinition();

      case 'SubleaseRequest':
        return this.getSubleaseRequestColumnDefinition();

      case 'Tenant':
        return this.getTenantColumnDefinition(null);

      case 'VacantRentalObject':
        return this.getVacantRentalObjectColumnDefinition();

      case 'VacantRentalObjectPremises':
        return this.getVacantRentalObjectPremisesColumnDefinition();

      case 'VacantRentalObjectDwelling':
        return this.getVacantRentalObjectDwellingColumnDefinition();

      case 'VacantRentalObjectParking':
        return this.getVacantRentalObjectParkingColumnDefinition();

      case 'OtherLeaseAgreementsColumnDefinition':
        return this.getOtherLeaseAgreementsColumnDefintion();

      case 'LeaseAgreementfaultNotification':
        return this.getLeaseAgreementfaultNotificationColumnDefinition();

      default:
        return null;
    }
  }
}
